import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
} from 'react-native';
import { theme } from '../../theme/theme';
import { SearchIcon, CloseIcon } from '../icons';

interface SearchSuggestionsProps {
  visible: boolean;
  suggestions: string[];
  recentSearches: string[];
  onSelectSuggestion: (suggestion: string) => void;
  onClearRecentSearches: () => void;
  onRemoveRecentSearch: (search: string) => void;
}

export const SearchSuggestions: React.FC<SearchSuggestionsProps> = ({
  visible,
  suggestions,
  recentSearches,
  onSelectSuggestion,
  onClearRecentSearches,
  onRemoveRecentSearch,
}) => {
  if (!visible) return null;

  const popularTags = [
    '油画', '水彩', '数字艺术', '摄影', '雕塑', 
    '抽象', '风景', '人物', '静物', '现代艺术'
  ];

  return (
    <View style={styles.container}>
      <ScrollView showsVerticalScrollIndicator={false}>
        {/* 热门标签 */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>热门标签</Text>
          <View style={styles.tagsContainer}>
            {popularTags.map((tag, index) => (
              <TouchableOpacity
                key={index}
                style={styles.tagItem}
                onPress={() => onSelectSuggestion(tag)}
              >
                <Text style={styles.tagText}>{tag}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {/* 搜索建议 */}
        {suggestions.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>搜索建议</Text>
            {suggestions.map((suggestion, index) => (
              <TouchableOpacity
                key={index}
                style={styles.suggestionItem}
                onPress={() => onSelectSuggestion(suggestion)}
              >
                <SearchIcon size={16} color={theme.colors.textSecondary} />
                <Text style={styles.suggestionText}>{suggestion}</Text>
              </TouchableOpacity>
            ))}
          </View>
        )}

        {/* 最近搜索 */}
        {recentSearches.length > 0 && (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>最近搜索</Text>
              <TouchableOpacity onPress={onClearRecentSearches}>
                <Text style={styles.clearText}>清除全部</Text>
              </TouchableOpacity>
            </View>
            {recentSearches.map((search, index) => (
              <View key={index} style={styles.recentItem}>
                <TouchableOpacity
                  style={styles.recentContent}
                  onPress={() => onSelectSuggestion(search)}
                >
                  <SearchIcon size={16} color={theme.colors.textSecondary} />
                  <Text style={styles.recentText}>{search}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.removeButton}
                  onPress={() => onRemoveRecentSearch(search)}
                >
                  <CloseIcon size={14} color={theme.colors.textSecondary} />
                </TouchableOpacity>
              </View>
            ))}
          </View>
        )}

        {/* 快速筛选 */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>快速筛选</Text>
          <TouchableOpacity
            style={styles.quickFilterItem}
            onPress={() => onSelectSuggestion('在售作品')}
          >
            <Text style={styles.quickFilterText}>🏷️ 在售作品</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.quickFilterItem}
            onPress={() => onSelectSuggestion('最新发布')}
          >
            <Text style={styles.quickFilterText}>🆕 最新发布</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.quickFilterItem}
            onPress={() => onSelectSuggestion('热门作品')}
          >
            <Text style={styles.quickFilterText}>🔥 热门作品</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
    maxHeight: 400,
  },
  section: {
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.sm,
  },
  sectionTitle: {
    fontSize: theme.fontSize.sm,
    fontWeight: theme.fontWeight.semibold,
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.sm,
  },
  clearText: {
    fontSize: theme.fontSize.xs,
    color: theme.colors.primary,
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -theme.spacing.xs,
  },
  tagItem: {
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: theme.spacing.xs,
    margin: theme.spacing.xs,
    backgroundColor: theme.colors.bg,
    borderRadius: theme.borderRadius.sm,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  tagText: {
    fontSize: theme.fontSize.sm,
    color: theme.colors.text,
  },
  suggestionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: theme.spacing.sm,
  },
  suggestionText: {
    fontSize: theme.fontSize.base,
    color: theme.colors.text,
    marginLeft: theme.spacing.sm,
  },
  recentItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: theme.spacing.sm,
  },
  recentContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  recentText: {
    fontSize: theme.fontSize.base,
    color: theme.colors.text,
    marginLeft: theme.spacing.sm,
  },
  removeButton: {
    padding: theme.spacing.xs,
  },
  quickFilterItem: {
    paddingVertical: theme.spacing.sm,
  },
  quickFilterText: {
    fontSize: theme.fontSize.base,
    color: theme.colors.text,
  },
});
